import {
  AnimatedDiv,
  ShowAnimation,
} from '@vimily/bonjoro-ui-components-v2/AnimatedDiv';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { usePlasmicCMS } from '~/App/Api/Plasmic';
import { PlasmicModelType, PlanCard } from '~/types/models/plasmic';
import { Title } from '../Components/Common/Title';
import { useCustomerCoupon } from '~/App/Api/Coupon';
import SeeAllInclusionsLink from '../Components/Common/SeeAllInclusionsLink';
import { PaymentGateway, Service } from '~/types/models';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { useSubscriptions } from '../Hooks/useSubscription';
import { useHistory, useRouteMatch } from 'react-router';
import { DiscountPill } from '../Components/Common/DiscountPill';
import { billingRoutes } from '..';
import qs from 'query-string';
import BillingCycleContext from '~/App/Contexts/BillingCycleContext';
import { PricingPlanCards } from '../Components/Pricing/PricingPlanCards';
import { RedeeemCouponCta } from '../Components/Common/RedeemCouponCta';
import { useMappedPlanCards } from '../Hooks/useMappedPlanCards';
import { PricingPlanCardLayout } from '../Components/Pricing/PricingPlanCards/PricingPlanCard';
import cx from 'classnames';
import useHideNavbar from '~/react-hooks/use-hide-navbar';
import { CheckoutType } from './Checkout';
import { canSwitchBillingCycle } from '~/react-hooks/use-plans';
import useCurrentTeam from '~/react-hooks/use-current-team';
import { BillingCycleToggler } from '../Components/Common/BillingCycleToggler';
import { openShopifyCheckoutPage } from '../utils';
import { UpgradeModalViewContext } from '~/App/Services/BillingAnalytics';
import useAnalytics from '~/react-hooks/use-analytics';
import { CHECKOUT_STARTED } from '~/generated/AnalyticsTypes';

export enum PricingSelectorType {
  VideoMessaging = `${Service.VideoMessaging}`,
  Testimonials = `${Service.Testimonials}`,
  Both = `${Service.VideoMessaging}-and-${Service.Testimonials}`,
}

export function Pricing() {
  const history = useHistory();
  const analytics = useAnalytics();
  const match = useRouteMatch<{ select?: PricingSelectorType }>();
  const { select = PricingSelectorType.VideoMessaging } = match.params;
  const [vmPlan, setVmPlan] = useState<string>();
  const [tmPlan, setTmPlan] = useState<string>();
  const { billingCycle, setBillingCycle } = useContext(BillingCycleContext);
  const { billing_cycle: teamBillingCycle, payment_gateway: paymentGateway } =
    useCurrentTeam();

  const checkoutContext = UpgradeModalViewContext.BillingPage;

  useHideNavbar();

  const isBothSelectorType = useMemo(
    () => select === PricingSelectorType.Both,
    [select]
  );
  const isVmSelectorType = useMemo(
    () => select === PricingSelectorType.VideoMessaging,
    [select]
  );
  const isTmSelectorType = useMemo(
    () => select === PricingSelectorType.Testimonials,
    [select]
  );

  useEffect(() => {
    setBillingCycle(teamBillingCycle);
  }, [teamBillingCycle]);

  // Push to Checkout page depending of the selector type and the checked value
  useEffect(() => {
    if (
      (isBothSelectorType && vmPlan && tmPlan) ||
      (isVmSelectorType && vmPlan) ||
      (isTmSelectorType && tmPlan)
    ) {
      const query = qs.stringify({
        items: [vmPlan, tmPlan],
        context: checkoutContext,
      });
      history.push(`${billingRoutes.checkout}${CheckoutType.Plans}?${query}`, {
        from: 'pricing',
      });
    }
  }, [isBothSelectorType, isVmSelectorType, isTmSelectorType, vmPlan, tmPlan]);

  // Checks the current Module type to show for selection depending on the selector type and the current plan selected
  const { service, isVmService, isTmService } = useMemo(() => {
    if ((isBothSelectorType && !vmPlan) || isVmSelectorType) {
      return {
        service: Service.VideoMessaging,
        isVmService: true,
        isTmService: false,
      };
    } else if ((isBothSelectorType && !tmPlan) || isTmSelectorType) {
      return {
        service: Service.Testimonials,
        isVmService: false,
        isTmService: true,
      };
    }

    return {
      service: undefined,
      isVmService: false,
      isTmSelectorType: false,
    };
  }, [isBothSelectorType, isVmSelectorType, isTmSelectorType, vmPlan, tmPlan]);

  const canSwithBillingCycle = canSwitchBillingCycle(service);

  const { data: subscriptions } = useSubscriptions({});
  const { data: coupon, isLoading } = useCustomerCoupon({});
  const { data: planCards } = usePlasmicCMS<PlanCard>({
    model: PlasmicModelType.PlanCard,
    keys: { service: service ?? '' },
    query: {
      category: 'pricing',
      service: service ?? '',
    },
  });
  const discountFormatted = subscriptions?.[0].discount_formatted; //all subscriptions has the same discounts

  const onPlanSelect = useCallback(
    (planSlug?: string) => {
      if (!planSlug) return;

      analytics.track(CHECKOUT_STARTED, { context: checkoutContext });

      if (paymentGateway == PaymentGateway.Shopify) {
        openShopifyCheckoutPage(planSlug);
        return;
      }

      if (service === Service.VideoMessaging) {
        setVmPlan(planSlug);
      } else if (service === Service.Testimonials) {
        setTmPlan(planSlug);
      }
    },
    [service, paymentGateway]
  );

  const mappedPricingPlanCards = useMappedPlanCards({
    plasmicPlanCards: planCards,
    coupon: coupon?.coupon,
    subscriptions,
    billingCycle,
    onPlanSelect,
  });

  const showCouponCodeCta =
    (!coupon || (coupon && Object.keys(coupon).length === 0)) && !isLoading;

  const titleLabel = isVmService
    ? 'Video messaging plans and pricing'
    : isTmService
    ? 'Testimonials plans and pricing'
    : 'Plans and pricing';

  const showDiscountPill = coupon?.coupon && subscriptions?.[0].discount;

  if (!service) return <></>;

  return (
    <AnimatedDiv
      showAnimation={ShowAnimation.FadeIn150}
      show
      className='flex flex-col gap-y-5 max-w-[1200px]'
    >
      <div className='flex flex-row justify-between'>
        <div className='flex flex-row flex-wrap justify-start items-center gap-x-4 gap-y-4'>
          {isBothSelectorType && isTmService && (
            <FontAwesomeIcon
              icon={faChevronLeft}
              className='text-[35px] cursor-pointer v2-text-neutral-900 hover:v2-text-yellow-500'
              onClick={() => setVmPlan(undefined)}
            />
          )}
          <Title label={titleLabel} />
          <RedeeemCouponCta show={showCouponCodeCta} />
          {showDiscountPill && discountFormatted && (
            <DiscountPill
              className='text-base !h-[30px] leading-5'
              label={`${discountFormatted} off applied`}
              service={service}
            />
          )}
        </div>
        <SeeAllInclusionsLink
          trackingContext='pricing'
          service={
            isVmSelectorType
              ? Service.VideoMessaging
              : isTmSelectorType
              ? Service.Testimonials
              : undefined
          }
        />
      </div>
      <div className='w-full flex flex-col gap-y-3 py-4 items-center'>
        {!(isBothSelectorType && isTmService) && canSwithBillingCycle && (
          <BillingCycleToggler
            billingCycle={billingCycle}
            setBillingCycle={setBillingCycle}
          />
        )}
        <PricingPlanCards
          pricingPlanCards={mappedPricingPlanCards}
          discounted={coupon?.coupon != undefined}
          billingCycle={billingCycle}
          className={cx('flex-wrap min-[1350px]:flex-nowrap', {
            'pt-6': isVmService,
          })}
          layout={
            isVmService
              ? PricingPlanCardLayout.Portrait
              : PricingPlanCardLayout.Landscape
          }
        />
      </div>
    </AnimatedDiv>
  );
}
